/**
 * Terms of Service Page (v0.10.0 - Issue #387)
 * 
 * Clear, modern terms of service for all users.
 * Features approachable TL;DR section and comprehensive legal coverage.
 */

import { useNavigate, Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import SectionGroup from '../components/SectionGroup';
import CalloutCard from '../components/CalloutCard';
import Divider from '../components/Divider';
import LastUpdated from '../components/LastUpdated';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <PageHeader
        title="Terms of Service"
        subtitle="The rules of the road for using ORO"
      />

      <CalloutCard title="TL;DR">
        <p className="text-text-secondary">
          Welcome to ORO MOVE<sup>™</sup>. By using this thing, you agree to play nice, not break stuff, and not blame us for the questionable decisions you make while running, biking, or otherwise moving your body. We provide tools; you provide common sense. Deal? Great.
        </p>
      </CalloutCard>

      <LastUpdated date="December 2025" />

      <Section>
        <p className="text-text-secondary leading-relaxed">
          Welcome to ORO. These Terms of Service ("Terms") govern your access to and use of the ORO platform, websites, services, applications, and related features (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
        </p>
        <p className="text-text-secondary leading-relaxed mt-4">
          If you do not agree, do not use the Service. (We'll miss you, but boundaries are healthy.)
        </p>
      </Section>

      <Divider />

      <SectionGroup>
        <Section 
          title="1. Who We Are"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            ORO provides tools that help clubs, communities, race organizers, and individuals connect, coordinate, and move together. We are a platform, not a coach, not a medical expert, and definitely not responsible for your shoelaces.
          </p>
        </Section>

        <Section 
          title="2. Eligibility"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              You must be at least <strong className="text-text-primary">13 years old</strong> to use the Service. If you're under the age of majority in your location, a parent/guardian must agree to these Terms on your behalf.
            </p>
            <p>Clubs and organizations must ensure their administrators are authorized to act for them.</p>
          </div>
        </Section>

        <Section 
          title="3. Your Account"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>You are responsible for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>Ensuring any information you provide is accurate</li>
              <li>All activity that occurs under your account</li>
            </ul>
            <p>If something weird happens under your account, tell us immediately. We take security seriously.</p>
          </div>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="4. Acceptable Use"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the Service for unlawful or harmful purposes</li>
              <li>Harass, threaten, or impersonate others</li>
              <li>Upload anything malicious, illegal, or wildly inappropriate</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Attempt to access restricted areas or data that isn't yours</li>
            </ul>
            <p>Basically: be a good human. Don't make us write a longer list.</p>
          </div>
        </Section>

        <Section 
          title="5. User Content"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              You may upload or submit photos, messages, event information, and other content ("User Content").
            </p>
            <p>
              By doing so, you grant ORO MOVE<sup>™</sup> a non-exclusive, worldwide, royalty-free license to host, use, display, reproduce, modify, and distribute that content solely for the operation of the Service.
            </p>
            <p>You remain the owner of your content.</p>
            <p>We reserve the right to remove content that violates these Terms or applicable laws.</p>
          </div>
        </Section>

        <Section 
          title="6. Privacy"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            Your privacy matters. Our{' '}
            <button 
              onClick={() => navigate('/privacy')}
              className="text-accent hover:text-accent-light transition-colors font-medium underline"
            >
              Privacy Policy
            </button>
            {' '}explains how we collect, use, and safeguard your information. By using the Service, you consent to our data practices as described there.
          </p>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="7. Clubs, Events & Interactions"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              ORO merely facilitates interactions between users, clubs, and organizers. We do not control, endorse, or guarantee:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>The accuracy of event information</li>
              <li>The safety or quality of any activity</li>
              <li>The qualifications of any person or organization listed on the platform</li>
            </ul>
            <p>Participation in any activity is at your own discretion and risk.</p>
          </div>
        </Section>

        <Section 
          title="8. Assumption of Risk"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              Running, cycling, and outdoor activities can involve risks (weather, terrain, questionable hydration strategies, etc.). By using the Service, you acknowledge these risks and agree that ORO, LLC is <strong className="text-text-primary">not liable</strong> for injuries, damages, or losses that occur during or because of any activity organized via the platform.
            </p>
            <p>We provide digital tools — the physical world is still up to you.</p>
          </div>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="9. Service Modifications"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            We may update, change, or discontinue features of the Service at any time. We try to avoid surprises, but we reserve the right to ship improvements without sending up a flare first.
          </p>
        </Section>

        <Section 
          title="10. Termination"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>We may suspend or terminate your access if:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You violate these Terms</li>
              <li>You misuse the platform</li>
              <li>You engage in harmful or illegal activities</li>
            </ul>
            <p>
              You may stop using the Service at any time. Certain rights survive termination (e.g., licensing, disclaimers, limitations of liability).
            </p>
          </div>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="11. Disclaimers"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              The Service is provided <strong className="text-text-primary">"as is"</strong> and <strong className="text-text-primary">"as available"</strong> with no warranties of any kind, express or implied.
            </p>
            <div>
              <p>We do not guarantee:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Uninterrupted service</li>
                <li>Bug-free code</li>
                <li>100% accuracy of content</li>
                <li>That your club will suddenly become wildly popular</li>
              </ul>
            </div>
            <p>Use the Service at your own discretion.</p>
          </div>
        </Section>

        <Section 
          title="12. Limitation of Liability"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>To the maximum extent permitted by law, ORO shall not be liable for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of data, opportunities, or goodwill</li>
              <li>Injuries or incidents during any event or activity</li>
              <li>Actions or omissions of clubs, organizers, or other users</li>
            </ul>
            <p>
              If we're legally responsible for anything, our total liability is limited to the amount you paid us in the last 12 months (which, for many users, is $0 — math is wild).
            </p>
          </div>
        </Section>

        <Section 
          title="13. Indemnification"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              You agree to indemnify and hold ORO harmless from any claims, damages, liabilities, and expenses arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your use of the Service</li>
              <li>Your User Content</li>
              <li>Your violation of these Terms</li>
            </ul>
            <p>Or, put simply: please don't make us hire extra lawyers.</p>
          </div>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="14. Third-Party Services"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            The Service may integrate with third-party tools or platforms. Your use of those is governed by their respective terms. We are not responsible for their content or actions.
          </p>
        </Section>

        <Section 
          title="15. Governing Law"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              These Terms are governed by the laws of the State of West Virginia, without regard to conflict-of-law principles.
            </p>
            <p>Any disputes will be resolved in state or federal courts located within West Virginia.</p>
          </div>
        </Section>

        <Section 
          title="16. Changes to These Terms"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              We may update these Terms periodically. If we make material changes, we'll let you know in a reasonable way (in-app notice, email, or similar).
            </p>
            <p>
              Your continued use of the Service after changes become effective means you accept the updated Terms.
            </p>
          </div>
        </Section>

        <Section 
          title="17. Contact"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            If you have questions, concerns, or find a clever typo we somehow missed,{' '}
            <Link 
              to="/contact" 
              className="text-accent hover:text-accent-light transition-colors font-medium underline"
            >
              get in touch
            </Link>.
          </p>
        </Section>
      </SectionGroup>
    </PageLayout>
  );
}